import type { Quiz } from '../services/api';

/** Title + optional EN + format — enough to resolve WITC display name. */
export type QuizTitleLike = Pick<Quiz, 'title'> & {
  titleEn?: string | null;
  format?: string;
};

/** Includes optional lobby subtitle lines (theme). */
export type QuizCardTextLike = QuizTitleLike & {
  theme?: string | null;
  themeEn?: string | null;
};

/** WITC and multiple-choice lobbies are bilingual (PT + EN) on the server. */
function isBilingualLobbyFormat(format: string | undefined): boolean {
  const f = (format ?? '').toLowerCase();
  return f === 'witc' || f === 'multiplechoice' || f === 'stop';
}

/** For bilingual quizzes, pick Portuguese or English title from UI language. */
export function getQuizDisplayTitle(quiz: QuizTitleLike, language: string): string {
  const lang = (language || 'en').toLowerCase();
  if (isBilingualLobbyFormat(quiz.format) && quiz.titleEn?.trim()) {
    return lang.startsWith('pt') ? quiz.title : quiz.titleEn.trim();
  }
  return quiz.title;
}

/**
 * Second line on lobby cards (optional). For WITC / MC, uses theme vs themeEn by UI language,
 * with fallback when only one locale is filled.
 */
export function getQuizDisplayTheme(quiz: QuizCardTextLike, language: string): string {
  const lang = (language || 'en').toLowerCase();
  const themePt = quiz.theme?.trim() ?? '';
  const themeEn = quiz.themeEn?.trim() ?? '';
  if (!isBilingualLobbyFormat(quiz.format)) {
    return themePt;
  }
  if (lang.startsWith('pt')) {
    return themePt || themeEn;
  }
  return themeEn || themePt;
}

/** Question or answer line: PT vs EN from UI language (MC + WITC play). */
export function pickBilingualLine(
  textPt: string,
  textEn: string | null | undefined,
  language: string
): string {
  const lang = (language || 'en').toLowerCase();
  const en = textEn?.trim();
  if (lang.startsWith('pt')) return textPt;
  return en || textPt;
}

/** Single-line label (e.g. select options, share text, image alt). */
export function getQuizFullDisplayCaption(quiz: QuizCardTextLike, language: string): string {
  const title = getQuizDisplayTitle(quiz, language);
  const theme = getQuizDisplayTheme(quiz, language);
  return theme ? `${title} — ${theme}` : title;
}
